import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useLocalization } from "../../../hooks/useLocalization";
import { useLocalePath } from "../../../hooks/useLocalePath";
import { useTeamInfo } from "../../../hooks/useTeams";
import { fetchMatchDetail } from "../../../services/services";
import { IMatchDetail } from "../../../types/types";
import { normalizeGameRecord } from "../../../utils/lolAssets";
import { gameIndexFromRiotGameId } from "../../../utils/matchDetail";
import { buildRosterByPuuid, resolveTeamSides } from "../../../utils/matchGameSides";

export type MatchDetailViewMode = "scoreboard" | "graphs";

export const useMatchDetail = () => {
  const { matchId = "" } = useParams();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const gameIdParam = searchParams.get("gameId");
  const { strings, locale } = useLocalization();
  const { internalPath } = useLocalePath();
  const copy = strings.MATCH_DETAIL;
  const teamInfos = useTeamInfo();
  const [detail, setDetail] = useState<IMatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGameIndex, setSelectedGameIndex] = useState(0);
  const [viewMode, setViewMode] = useState<MatchDetailViewMode>("scoreboard");
  const mountedRef = useRef(true);
  const parsedId = Number(matchId);

  useEffect(() => {
    if (!Number.isFinite(parsedId) || parsedId <= 0) {
      setLoading(false);
      setError(copy.INVALID_MATCH);
      return;
    }

    mountedRef.current = true;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const response = await fetchMatchDetail(parsedId);
        if (!mountedRef.current) return;
        if (!response?.match) {
          setError(copy.NOT_FOUND);
          setDetail(null);
          return;
        }
        const games = (response.games ?? []).map((game) => normalizeGameRecord(game));
        setDetail({
          ...response,
          games,
          playerStats: response.playerStats ?? [],
        });
        const parsedGameId = gameIdParam ? Number(gameIdParam) : NaN;
        const gameIndex =
          Number.isFinite(parsedGameId) && parsedGameId > 0
            ? gameIndexFromRiotGameId(games, parsedGameId)
            : null;
        setSelectedGameIndex(gameIndex ?? 0);
        setViewMode("scoreboard");
      } catch (err) {
        console.error("Failed to load match detail:", err);
        if (!mountedRef.current) return;
        setError(copy.LOAD_ERROR);
        setDetail(null);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [parsedId, gameIdParam, copy.INVALID_MATCH, copy.NOT_FOUND, copy.LOAD_ERROR]);

  const rosterByPuuid = useMemo(() => {
    const players = teamInfos?.flatMap((team) => team.players ?? []) ?? [];
    return buildRosterByPuuid(players);
  }, [teamInfos]);

  const games = detail?.games ?? [];
  const playerStats = detail?.playerStats ?? [];

  const gameMappings = useMemo(() => {
    if (!detail?.match || !games.length) return [];
    return games.map((game) =>
      resolveTeamSides(
        game,
        detail.match.teamOne,
        detail.match.teamTwo,
        rosterByPuuid,
        playerStats.filter((stat) => stat.riotGameId === game.game_id),
      ),
    );
  }, [detail?.match, games, playerStats, rosterByPuuid]);

  const seoTitle = detail
    ? `${detail.match.teamOneName} vs ${detail.match.teamTwoName}`
    : strings.SEO.MATCH.TITLE;
  const seoDescription = detail
    ? `${detail.match.teamOneName} ${String(detail.match.teamOneScore)}–${String(detail.match.teamTwoScore)} ${detail.match.teamTwoName}. ${strings.SEO.MATCH.DESCRIPTION}`
    : strings.SEO.MATCH.DESCRIPTION;
  const matchHeadingId = "match-detail-heading";

  return {
    loading,
    error,
    detail,
    selectedGameIndex,
    setSelectedGameIndex,
    viewMode,
    setViewMode,
    games,
    playerStats,
    gameMappings,
    rosterByPuuid,
    seoTitle,
    seoDescription,
    matchHeadingId,
    copy,
    strings,
    locale,
    pathname,
    internalPath,
    teamInfos,
  };
};
